import { Form, Drawer, Switch, Button, Tag } from "antd";
import { UserRoleMatrix } from "./RoleMatrix/UserRoleMatrix";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useEffect, useState } from "react";
import { auth } from "../../API/Auth";
import * as styles from "./UserContactCreationStyle";
import { user } from "../../API/UserManagement/User";
import { getUsers } from "../../slices/User/Helpers/GetAllUsers";
import ReusableForm from "../../unity-ui-common/Components/ReForm";
import ReInput from "../../unity-ui-common/Components/ReFormFields/ReInput";
import ReSelect from "../../unity-ui-common/Components/ReFormFields/ReSelect";
import { ReNotification } from "../../unity-ui-common/Components/ReNotification";
import { role } from "../../API/UserManagement/Role";
import { tenant } from "../../API/UserManagement/Tenant";
import { convertDateInReadableFormat, GetRoleName } from "../../Helper/Methods";
import { ICreateUser } from "../../Interfaces/Apis/User.interface";
import { loadState } from "../../sessionstorage";
import { ruleEngine } from "../../API/RuleEngine";
import { ModeOfCorrespondence } from "./common";
import { mode } from "crypto-js";
import { study } from "../../API/UserManagement/Study";
import ReUpload from "../../unity-ui-common/Components/ReFormFields/ReUpload";
import { file } from "../../API/File";

// A contact carries a single site with one or more studies. Studies are stored
// and displayed as a semi-colon separated list, while the Study Number
// multi-select works with an array. Reading also accepts "," and "|": rows
// written before the switch to ";" are comma separated and the reports render
// them pipe separated.
const STUDY_SEPARATOR = ";";
const STUDY_SEPARATORS = /[;,|]/;

const splitOn = (value: any, separator: string | RegExp): string[] => {
  if (value === null || value === undefined) return [];
  const parts = Array.isArray(value) ? value : String(value).split(separator);
  return parts.map((item: any) => String(item ?? "").trim()).filter(Boolean);
};

const splitStudyNumbers = (value: any): string[] => splitOn(value, STUDY_SEPARATORS);

const formatStudyNumber = (value: any): string =>
  splitStudyNumbers(value).join(STUDY_SEPARATOR);

export const UserContactCreationForm = (props: {
  drawerType: string;
  visibility: boolean;
  setUserDrawerType: Function;
  setUserDrawerVisibility: Function;
  currEditingUser: any;
  userData: any;
  setCommonRefresh: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [form] = Form.useForm();
  const {
    drawerType,
    visibility,
    setUserDrawerType,
    setUserDrawerVisibility,
    currEditingUser,
    userData,
    setCommonRefresh,
  } = props;

  const dispatch = useAppDispatch();
  const selectedTenant = useAppSelector((state) => state.session.selectedTenant);
  const dataEntryMode =
    (selectedTenant as any)?.configs?.dataEntryMode || "Hybrid";
  const isManual = dataEntryMode === "Manual";
  // Site Number / Site Name / Study Number are editable in every mode.
  //  • Centralized/Hybrid — a contact owns one site, the drawer shows that row's
  //    own values and the server applies the edit to every row the contact owns.
  //  • Manual — a contact keeps one record per site/study/role and the view
  //    shows them merged. Editing starts from the single record behind the grid
  //    row (record_* values) and the server writes only to that record, so the
  //    merged list is never written back and the other records keep theirs.
  const siteFieldValues = (forEdit: boolean) => {
    const own = forEdit && isManual;
    const studies = own ? currEditingUser?.record_study_number : currEditingUser?.study_number;
    return {
      siteNumber: own ? currEditingUser?.record_site_number : currEditingUser?.site_number,
      siteName: own ? currEditingUser?.record_site_name : currEditingUser?.site_name,
      // Manual mode edits the studies as free text; elsewhere the field is a
      // multi-select over the Study Library and needs the list form.
      studyNumber: isManual ? formatStudyNumber(studies) : splitStudyNumbers(studies),
    };
  };
  const initialFormValues = {
    name: "",
    email: "",
    phoneNumber: "",
    roleMatrices: [{ roles: [""], tenant: "" }],
    status: 1,
  };
  currEditingUser.roleMatrices = currEditingUser.role_matrices;
  const [selectedRoles, setSelectedRoles] = useState<any[]>([]);
  const [selectedSponsors, setSelectedSponsors] = useState<any[]>([]);
  const [tempUser, setTempUser] = useState<ICreateUser>(initialFormValues);
  const [allRoles, setAllRoles] = useState<any[]>([]);
  const [allStudies, setAllStudies] = useState<any[]>([]);
  const [allSponsors, setAllSponsors] = useState<any>([]);
  const [editable, setEditable] = useState(false);
  const [country, setCountry] = useState<any>([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [attachmentList, setAttachmentList] = useState<any>([]);
  const [attachmentformData, setAttachmentformData] = useState<any>([]);
  const [submitting, setSubmitting] = useState(false);
  const [attachmentData, setAttachmentData] = useState<any>([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  // Contact Name is shared by every sponsor, so only a Super Admin (20) or a
  // Safety Team Administrator (12) may change it - the server enforces the same.
  const [canRenameContact, setCanRenameContact] = useState(false);
  const [selectedTargetTenant, setSelectedTargetTenant] = useState<string[]>([]);

  useEffect(() => {
    setEditable(drawerType == "create" || drawerType == "edit");
  }, [drawerType]);
  const handleSelectChanges = (type: string, value: string[]) => {
    // debugger;
    const data = value.map((id: string) => ({
      name: GetRoleName(id, type === "role" ? allRoles : allSponsors) || id,
      value: id,
    }));

    if (type === "role") {
      setSelectedRoles(data);

      if (isSuperAdmin) {
        // SuperAdmin: when a role is removed from dropdown, remove it from ALL tenant matrices
        // When a role is added, leave matrices unchanged (user must tick manually in matrix)
        setTempUser((prevData) => ({
          ...prevData,
          roleMatrices: (prevData.roleMatrices || []).map((matrix) => ({
            ...matrix,
            roles: (matrix.roles || []).filter((r) => value.includes(r)),
          })),
        }));
      } else {
        // Non-SuperAdmin: update only the current tenant's matrix
        const currentTenantId = JSON.parse(
          sessionStorage.getItem("selectedTenant") || "{}"
        )?.id;

        let updatedRoleMatrices = tempUser.roleMatrices ? [...tempUser.roleMatrices] : [];
        const currentMatrixIdx = updatedRoleMatrices.findIndex(m => String(m.tenant) === String(currentTenantId));
        if (currentMatrixIdx >= 0) {
          updatedRoleMatrices[currentMatrixIdx] = {
            ...updatedRoleMatrices[currentMatrixIdx],
            roles: value
          };
        } else if (currentTenantId) {
          updatedRoleMatrices.push({
            tenant: currentTenantId,
            roles: value
          });
        }

        setTempUser((prevData) => ({
          ...prevData,
          roleMatrices: updatedRoleMatrices,
        }));
      }
    } else {
      setSelectedSponsors(data);
      setTempUser((prevData) => {
        const updatedMatrices = value.map((id) => {
          const existing = prevData.roleMatrices?.find((m) => m.tenant === id);
          return existing || { tenant: id, roles: [] };
        });
        return { ...prevData, roleMatrices: updatedMatrices };
      });
    }
  };

  const onClose = () => {
    setUserDrawerType("create");
    setUserDrawerVisibility(false);
    form.resetFields();
  };

  const handleFormSubmit = async (values: any) => {
    // debugger;
    setCommonRefresh(false);
    setSubmitLoading(true);
    if (values?.phoneNumber && values?.phoneNumber?.toString()?.length !== 10) {
      ReNotification({
        description: "Enter A Valid Phone Number",
        type: "error",
      });
      return;
    }
    values.attachmentModeOfCorrespondence = [
      ...attachmentformData,
      ...attachmentData,
    ];
    const payload: any = {
      name: values?.name,
      email: values?.email?.trim()?.toLowerCase() || "",
      phoneNumber: values.phoneNumber?.toString() || "",
      country: values?.country,
      roleMatrices: tempUser?.roleMatrices,
      ModeOfCorrespondence: values?.ModeOfCorrespondence,
      status: tempUser?.status,
      targetTenantIds:
        isSuperAdmin && selectedTargetTenant.length > 0
          ? selectedTargetTenant
          : undefined,
    };
    // Site/study are omitted from the payload — leaving the stored values alone —
    // when the form never held a value (null/undefined). Only "" means the user
    // actually emptied a rendered input, and that is what clears the field.
    const sendSiteLevel = (key: string, value: any, formatted?: string) => {
      if (value === undefined || value === null) return;
      payload[key] = formatted !== undefined ? formatted : value;
    };
    sendSiteLevel("siteNumber", values?.siteNumber);
    sendSiteLevel("siteName", values?.siteName);
    sendSiteLevel("studyNumber", values?.studyNumber, formatStudyNumber(values?.studyNumber));
    if (values?.ModeOfCorrespondence === "Email") {
      payload.emailIDs = values?.emailIDs
        ?.split(",")
        ?.map((email: any) => email?.trim()?.toLowerCase());
    } else if (values?.ModeOfCorrespondence === "Fax") {
      payload.faxNumber = values?.faxNumber;
    } else if (values?.ModeOfCorrespondence === "Gateway") {
      payload.formOfReport = values?.formOfReport;

      if (tempUser?.formOfReport === "XML") {
        payload.xmlDocType = values?.xmlDocType;
        payload.senderInfo = {
          firstName: values?.senderFirstName,
          lastName: values?.senderLastName,
          address: values?.senderAddress,
          city: values?.senderCity,
          state: values?.senderState,
          postalCode: values?.senderPostalCode,
          country: values?.senderCountry,
          interchangeId: values?.senderInterchangeID,
        };

        payload.receiverInfo = {
          firstName: values?.receiverFirstName,
          lastName: values?.receiverLastName,
          address: values?.receiverAddress,
          city: values?.receiverCity,
          state: values?.receiverState,
          postalCode: values?.receiverPostalCode,
          country: values?.receiverCountry,
          interchangeId: values?.receiverInterchangeID,
        };
      }
    }

    if (tempUser?.formOfReport === "XML" || tempUser?.formOfReport === "PDF") {
      payload.attachmentModeOfCorrespondence =
        values?.attachmentModeOfCorrespondence;
    }
    if (drawerType === "create") {
      let res = await user.createContact(payload);
      if (res?.statusCode > 300 || res?.status === false) {
        ReNotification({
          description: res?.message || "Something went wrong",
          type: "error",
        });
        setSubmitLoading(false);
        return;
      }

      ReNotification({
        description: "User Created Successfully",
        type: "success",
      });
      setCommonRefresh(true);
      setUserDrawerType("create");
      setSubmitLoading(false);
      setUserDrawerVisibility(false);
      onClose();
      dispatch(getUsers());
    } else {
      const isRename =
        String(values?.name ?? "").trim() !== String(currEditingUser?.name ?? "").trim();
      if (isRename && !canRenameContact) {
        const message =
          "Only a Super Admin or Safety Team Administrator can change the contact name.";
        form.setFields([{ name: "name", errors: [message] }]);
        ReNotification({ description: message, type: "error" });
        setSubmitLoading(false);
        return;
      }
      const updatePayload = { ...payload, id: currEditingUser.id };
      let res = await user.updateContact(
        currEditingUser.user_id,
        updatePayload
      );
      res = await res?.json();
      if (res?.statusCode > 300) {
        ReNotification({
          description: res?.message || "Something went wrong",
          type: "error",
        });
        setSubmitLoading(false);
        return;
      }

      dispatch(getUsers());
      ReNotification({
        description: "User Updated Successfully",
        type: "success",
      });
      setCommonRefresh(true);
      setSubmitLoading(false);
      setUserDrawerType("create");
      setUserDrawerVisibility(false);
      onClose();
    }
  };

  const getRoles = async () => {
    const res = await role.getAll();
    setAllRoles(Array.isArray(res) ? res : []);
  };

  const getStudies = async () => {
    const res = await study.getAllStudies();
    setAllStudies(Array.isArray(res) ? res : []);
  };
  const getSponsors = async () => {
    const res = await tenant.getAll();
    setAllSponsors(Array.isArray(res) ? res : []);
  };

  const getCountries = async () => {
    const res = await ruleEngine.getCountries();
    setCountry(Array.isArray(res) ? res : []);
  };

  const onBeforeAttachmentUpload = async (fileformData: any) => {
    setSubmitting(true);

    const filenameArr = fileformData["name"].split(".");
    const file_type = filenameArr[filenameArr.length - 1];

    const res = await file.getUrl(file_type);
    const fileName = filenameArr[0];
    setAttachmentList((prev: any) => [...prev, fileformData]);
    await file.remoteUploadFile(res.url, fileformData);
    setAttachmentformData((prev: any) => [
      ...prev,
      {
        fileUID: fileformData.uid,
        id: res.id,
        filename: fileName,
        file_type,
      },
    ]);
    setSubmitting(false);
  };

  const handleAttachmentFileRemove = (fileformData: any) => {
    setSubmitting(true);
    const index = attachmentList.indexOf(fileformData);
    const newFileList = attachmentList.slice();
    newFileList.splice(index, 1);
    setAttachmentList(newFileList);
    setAttachmentformData((prev: any) =>
      prev.filter((item: any) => item.fileUID !== fileformData.uid)
    );
    setSubmitting(false);
  };

  useEffect(() => {
    getRoles();
    getSponsors();
    getCountries();
    getStudies();

    const sessionData = loadState()?.session?.userData;
    const privileges = auth.getUsersPrivileges();
    const hasAdminPermission = privileges.includes("20");

    if (hasAdminPermission) {
      setIsSuperAdmin(true);
    }
    setCanRenameContact(hasAdminPermission || privileges.includes("12"));
  }, []);

  const handleRefresh = (forEdit: boolean = editable) => {
    if (drawerType === "view" || drawerType === "edit") {
      const tenantId = JSON.parse(
        sessionStorage.getItem("selectedTenant") || "{}"
      )?.id;
      const alreadySelectedSponsors = (userData?.roleMatrices || []).map((ele: any) => ({
        name: GetRoleName(ele.tenant, allSponsors) || ele.tenant,
        value: ele.tenant,
      }));

      // Both SuperAdmin and Non-SuperAdmin: matrix shows only selected sponsors
      // SuperAdmin selected sponsors come from Sponsor/MAH dropdown (userData.roleMatrices)
      // Non-SuperAdmin: only their current tenant
      const privileges = auth.getUsersPrivileges();
      const isAdmin = privileges.includes("20");
      if (isAdmin) {
        // Show only the sponsors that are already assigned (from Sponsor/MAH field)
        setSelectedSponsors(alreadySelectedSponsors);
      } else {
        const currentTenant = JSON.parse(
          sessionStorage.getItem("selectedTenant") || "{}"
        );
        setSelectedSponsors(
          currentTenant.id
            ? [{ name: currentTenant.name || "Current Tenant", value: currentTenant.id }]
            : alreadySelectedSponsors
        );
      }
      setSelectedTargetTenant((userData?.roleMatrices || []).map((ele: any) => ele.tenant));
      // findinig already have roles

      const uniqueRoleIds: any = [];
      // SuperAdmin: collect roles from ALL sponsors so they can see and assign
      // roles for any sponsor in the matrix.
      // Non-SuperAdmin: only collect roles from their current tenant — they must
      // never see roles belonging to other tenants.
      const sourceRoleMatrices = isAdmin
        ? userData?.roleMatrices || []
        : (userData?.roleMatrices || []).filter(
            (ele: any) => String(ele.tenant) === String(tenantId)
          );
      sourceRoleMatrices.forEach((ele: any) => {
        ele.roles?.forEach((id: string) => {
          if (!uniqueRoleIds.includes(id)) {
            uniqueRoleIds.push(id);
          }
        });
      });

      const alreadySelectedRoles = uniqueRoleIds.map((id: string) => ({
        name: GetRoleName(id, allRoles) || id,
        value: id,
      }));

      setSelectedRoles(alreadySelectedRoles);

      const rawStatus = currEditingUser?.status;
      const normalizedStatus =
        rawStatus === 1 ||
          rawStatus === "1" ||
          rawStatus === true ||
          rawStatus === "true" ||
          (typeof rawStatus === "string" && rawStatus.toLowerCase() === "active")
          ? 1
          : 0;
      setTempUser({
        name: currEditingUser?.name,
        email: currEditingUser?.email,
        phoneNumber: currEditingUser?.phoneNumber,
        roleMatrices: userData?.roleMatrices,
        status: normalizedStatus,
        modeOfCorrespondence: currEditingUser?.mode_of_correspondence,
        formOfReport: currEditingUser?.form_of_report,
        xmlDocType: currEditingUser?.xml_doc_type,
      });
      form.setFieldsValue({
        name: currEditingUser?.name,
        email: currEditingUser?.email,
        country: currEditingUser?.country,
        phoneNumber: currEditingUser?.phoneNumber,
        roleMatrices: currEditingUser?.roleMatrices,
        status: normalizedStatus,
        role: alreadySelectedRoles,
        sponsor: alreadySelectedSponsors,
        ...siteFieldValues(forEdit),
        ModeOfCorrespondence: currEditingUser?.mode_of_correspondence,
        formOfReport: currEditingUser?.form_of_report,
        xmlDocType: currEditingUser?.xml_doc_type,
        faxNumber: currEditingUser?.fax_number,
        senderFirstName: currEditingUser?.sender_info?.firstName,
        senderLastName: currEditingUser?.sender_info?.lastName,
        senderAddress: currEditingUser?.sender_info?.address,
        senderCity: currEditingUser?.sender_info?.city,
        senderState: currEditingUser?.sender_info?.state,
        senderPostalCode: currEditingUser?.sender_info?.postalCode,
        senderCountry: currEditingUser?.sender_info?.country,
        senderInterchangeID: currEditingUser?.sender_info?.interchangeId,
        receiverFirstName: currEditingUser?.receiver_info?.firstName,
        receiverLastName: currEditingUser?.receiver_info?.lastName,
        receiverAddress: currEditingUser?.receiver_info?.address,
        receiverCity: currEditingUser?.receiver_info?.city,
        receiverState: currEditingUser?.receiver_info?.state,
        receiverPostalCode: currEditingUser?.receiver_info?.postalCode,
        receiverCountry: currEditingUser?.receiver_info?.country,
        receiverInterchangeID: currEditingUser?.receiver_info?.interchangeId,
        emailIDs: Array.isArray(currEditingUser?.email_ids)
          ? currEditingUser.email_ids.join(",")
          : "",
        selectedTargetTenant: (userData?.roleMatrices || []).map((ele: any) => ele.tenant),
      });
    } else {
      const currentTenant = JSON.parse(
        sessionStorage.getItem("selectedTenant") || "{}"
      );
      const privileges = auth.getUsersPrivileges();
      const isAdmin = privileges.includes("20");

      // SuperAdmin: matrix shows sponsors selected in Sponsor/MAH dropdown (initially empty)
      // Non-SuperAdmin: only current tenant
      const matrixSponsors = isAdmin
        ? [] // SuperAdmin starts empty; sponsors populate when they select from Sponsor/MAH dropdown
        : currentTenant.id
          ? [{ name: currentTenant.name || "Default Sponsor", value: currentTenant.id }]
          : [];

      setTempUser({
        ...initialFormValues,
        roleMatrices: matrixSponsors.map((s: any) => ({
          tenant: s.value,
          roles: [],
        })),
      });
      setSelectedRoles([]);
      setSelectedSponsors(matrixSponsors);
      form.setFieldsValue({ initialFormValues });
    }
  };

  const handleModeChange = (value: string) => {
    setTempUser((prevData) => ({
      ...prevData,
      modeOfCorrespondence: value,
      formOfReport: "",
    }));
  };

  const handleGateWayChange = (value: string) => {
    setTempUser((prevData) => ({
      ...prevData,
      formOfReport: value,
    }));
    setAttachmentformData([]);
    setAttachmentData([]);
    setAttachmentList([]);
  };

  const handleTypeChange = (value: string) => {
    setTempUser((prevData) => ({
      ...prevData,
      xmlDocType: value,
    }));
    setAttachmentformData([]);
    setAttachmentData([]);
    setAttachmentList([]);
  };

  useEffect(() => {
    if (drawerType === "view" || drawerType === "edit") {
      handleRefresh();
    }
    // currEditingUser carries every non-role field the form is prefilled with —
    // without it a row selected after the drawer type settled would keep the
    // previously opened contact's values (or none at all).
  }, [drawerType, currEditingUser, userData, allSponsors, allRoles]);


  const getAttachmentData = async (attachment: string[]) => {
    if (!Array.isArray(attachment)) {
      return;
    }
    const fileData = await Promise.all(
      attachment?.map(async (id: string) => {
        const { fileRecord } = await file.getFileData(id);
        return {
          // fileUID: fileRecord.id,
          id: fileRecord.id,
          file_type: fileRecord.file_type,
          filename: fileRecord.filename,
        };
      })
    );
    setAttachmentData(fileData);
  };

  useEffect(() => {
    if (drawerType === "view") {
      getAttachmentData(currEditingUser?.attachment_mode_of_correspondence);
    }
  }, [currEditingUser, drawerType]);

  return (
    <styles.Container>
      <div className="common-flex-row justify-end user-creation-form">
        <Drawer
          placement="right"
          onClose={onClose}
          open={visibility}
          width="1000"
          title={drawerType === "edit" ? "Edit Contact" : "Contact Name"}
        >
          <ReusableForm
            formInstance={form}
            onSubmit={handleFormSubmit}
            className="form"
          // submitBtn
          // submitBtnText={editable? drawerType === "edit" ? "Update" : "Create":""}
          >
            {!editable && (
              <div className="formItem">
                <span>Contact Id: </span>
                <div
                  className="formData"
                  style={{ paddingTop: "4px", paddingBottom: "5px" }}
                >
                  <styles.ViewOnlyDiv>
                    {currEditingUser?.id ? currEditingUser?.id : "-"}
                  </styles.ViewOnlyDiv>
                </div>
              </div>
            )}

            <div className="formItem">
              <span>
                <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                Contact Name:{" "}
              </span>
              {editable ? (
                <div className="formInput">
                  <ReInput
                    name="name"
                    label=""
                    type="simple"
                    textItem="Contact Name"
                    required
                  />
                </div>
              ) : form.getFieldValue("name") ? (
                <div
                  className="formData"
                  style={{ paddingTop: "4px", paddingBottom: "5px" }}
                >
                  <styles.ViewOnlyDiv>
                    {form.getFieldValue("name")}
                  </styles.ViewOnlyDiv>
                </div>
              ) : (
                <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
              )}
            </div>
            {/* <ReInput label="Name" name="name" type="simple" required /> */}

            <div className="formItem">
              <span>
                <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                Contact Country:{" "}
              </span>
              {editable ? (
                <div className="formInput">
                  {/* <ReInput
                    name="country"
                    label=""
                    type="simple"
                    textItem="Contact Country"
                    required
                  /> */}
                  <ReSelect
                    label=""
                    name="country"
                    items={country?.map((rol: any) => {
                      return {
                        title: rol?.name,
                        value: rol?.name,
                      };
                    })}
                    textItem="Country"
                    required
                    searchable
                  />
                </div>
              ) : form.getFieldValue("country") ? (
                <div
                  className="formData"
                  style={{ paddingTop: "4px", paddingBottom: "5px" }}
                >
                  <styles.ViewOnlyDiv>
                    {form.getFieldValue("country")}
                  </styles.ViewOnlyDiv>
                </div>
              ) : (
                <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
              )}
            </div>
            <div className="formItem">
              <span>
                <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                Email ID:{" "}
              </span>
              {editable ? (
                <div className="formInput">
                  <ReInput
                    name="email"
                    label=""
                    type="simple"
                    textItem="Email ID"
                    required
                  />
                </div>
              ) : form.getFieldValue("email") ? (
                <div
                  className="formData"
                  style={{ paddingTop: "4px", paddingBottom: "5px" }}
                >
                  <styles.ViewOnlyDiv>
                    {form.getFieldValue("email")}
                  </styles.ViewOnlyDiv>
                </div>
              ) : (
                <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
              )}
            </div>

            <div className="formItem">
              <span>
                <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                Mode Of Correspondence:{" "}
              </span>
              {editable ? (
                <div className="formInput">
                  <ReSelect
                    label=""
                    name="ModeOfCorrespondence"
                    items={ModeOfCorrespondence?.map((rol: any) => {
                      return {
                        title: rol?.title,
                        value: rol?.value,
                      };
                    })}
                    onChange={handleModeChange}
                    textItem="ModeOfCorrespondence"
                    required
                    searchable
                  />
                </div>
              ) : form.getFieldValue("ModeOfCorrespondence") ? (
                <div
                  className="formData"
                  style={{ paddingTop: "4px", paddingBottom: "5px" }}
                >
                  <styles.ViewOnlyDiv>
                    {form.getFieldValue("ModeOfCorrespondence")}
                  </styles.ViewOnlyDiv>
                </div>
              ) : (
                <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
              )}
            </div>

            <div>
              {tempUser?.modeOfCorrespondence === "Email" && (
                <div className="formItem">
                  <span>
                    <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                    Email IDs:{" "}
                  </span>
                  {editable ? (
                    <div className="formInput">
                      <ReInput
                        name="emailIDs"
                        label=""
                        type="simple"
                        textItem="Email IDs"
                        required
                      />
                    </div>
                  ) : form.getFieldValue("emailIDs") ? (
                    <div
                      className="formData"
                      style={{ paddingTop: "4px", paddingBottom: "5px" }}
                    >
                      <styles.ViewOnlyDiv>
                        {form.getFieldValue("emailIDs")}
                      </styles.ViewOnlyDiv>
                    </div>
                  ) : (
                    <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
                  )}
                </div>
              )}

              {tempUser?.modeOfCorrespondence === "Fax" && (
                <div className="formItem">
                  <span>
                    <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                    Fax:{" "}
                  </span>

                  {editable ? (
                    <div className="formInput">
                      <ReInput
                        name="faxNumber"
                        label=""
                        type="simple"
                        textItem="Fax Number"
                        required
                      />
                    </div>
                  ) : form.getFieldValue("faxNumber") ? (
                    <div
                      className="formData"
                      style={{ paddingTop: "4px", paddingBottom: "5px" }}
                    >
                      <styles.ViewOnlyDiv>
                        {form.getFieldValue("faxNumber")}
                      </styles.ViewOnlyDiv>
                    </div>
                  ) : (
                    <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
                  )}
                </div>
              )}

              {(tempUser?.modeOfCorrespondence === "Gateway" ||
                tempUser?.modeOfCorrespondence === "SFTP") && (
                  <div className="formItem">
                    <span>
                      <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                      Format of Report:{" "}
                    </span>

                    {editable ? (
                      <div className="formInput">
                        {tempUser?.modeOfCorrespondence === "Gateway" ? (
                          <ReSelect
                            label=""
                            name="formOfReport"
                            items={[
                              { name: "XML", value: "XML" },
                              { name: "PDF", value: "PDF" },
                            ]?.map((rol: any) => {
                              return {
                                title: rol.name,
                                value: rol.value,
                              };
                            })}
                            onChange={handleGateWayChange}
                            textItem="Form of Report"
                            required
                            searchable
                          />
                        ) : (
                          <ReSelect
                            label=""
                            name="formOfReport"
                            items={[
                              { name: "XML", value: "XML" },
                              { name: "PDF", value: "PDF" },
                              { name: "CSV", value: "CSV" },
                              { name: "Excel", value: "Excel" },
                            ]?.map((rol: any) => {
                              return {
                                title: rol.name,
                                value: rol.value,
                              };
                            })}
                            onChange={handleGateWayChange}
                            textItem="Form of Report"
                            required
                            searchable
                          />
                        )}
                      </div>
                    ) : form.getFieldValue("formOfReport") ? (
                      <div
                        className="formData"
                        style={{ paddingTop: "4px", paddingBottom: "5px" }}
                      >
                        <styles.ViewOnlyDiv>
                          {form.getFieldValue("formOfReport")}
                        </styles.ViewOnlyDiv>
                      </div>
                    ) : (
                      <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
                    )}
                  </div>
                )}

              {editable &&
                (tempUser?.formOfReport === "XML" ||
                  tempUser?.formOfReport === "PDF" ||
                  tempUser?.formOfReport === "CSV" ||
                  tempUser?.formOfReport === "Excel") && (
                  <div className="formItem" style={{ paddingBottom: "1.5rem" }}>
                    <span>
                      <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                      Attach File:{" "}
                    </span>
                    <ReUpload
                      errorMessage="Please enter your file"
                      accept={
                        tempUser?.formOfReport === "XML"
                          ? ".xml"
                          : tempUser?.formOfReport === "CSV"
                            ? ".csv"
                            : tempUser?.formOfReport === "Excel"
                              ? ".xls,.xlsx"
                              : ".pdf"
                      }
                      label=""
                      BtnTitle="Click to Upload File"
                      name="attachmentModeOfCorrespondence"
                      onBeforeUpload={onBeforeAttachmentUpload}
                      onRemove={handleAttachmentFileRemove}
                      fileListMaxCount={1}
                      fileList={attachmentList}
                      required
                    />
                    {attachmentData?.map((ele: any) => {
                      return (
                        <Tag
                          closable
                          onClose={() => {
                            setAttachmentData(
                              attachmentData.filter(
                                (att: any) => att.id !== ele.id
                              )
                            );
                          }}
                          key={ele.id}
                        >
                          {ele.filename}
                        </Tag>
                      );
                    })}
                  </div>
                )}

              {tempUser?.formOfReport === "XML" && (
                <>
                  <div className="formItem">
                    <span>
                      <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                      Sender First Name:{" "}
                    </span>
                    {editable ? (
                      <div className="formInput">
                        <ReInput
                          name="senderFirstName"
                          label=""
                          type="simple"
                          textItem="Sender First Name"
                          required
                        />
                      </div>
                    ) : form.getFieldValue("senderFirstName") ? (
                      <div
                        className="formData"
                        style={{ paddingTop: "4px", paddingBottom: "5px" }}
                      >
                        <styles.ViewOnlyDiv>
                          {form.getFieldValue("senderFirstName")}
                        </styles.ViewOnlyDiv>
                      </div>
                    ) : (
                      <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
                    )}
                  </div>

                  <div className="formItem">
                    <span>
                      <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                      Sender Last Name:{" "}
                    </span>
                    {editable ? (
                      <div className="formInput">
                        <ReInput
                          name="senderLastName"
                          label=""
                          type="simple"
                          textItem="Sender Last Name"
                          required
                        />
                      </div>
                    ) : form.getFieldValue("senderLastName") ? (
                      <div
                        className="formData"
                        style={{ paddingTop: "4px", paddingBottom: "5px" }}
                      >
                        <styles.ViewOnlyDiv>
                          {form.getFieldValue("senderLastName")}
                        </styles.ViewOnlyDiv>
                      </div>
                    ) : (
                      <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
                    )}
                  </div>

                  <div className="formItem">
                    <span>
                      <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                      Sender Address:{" "}
                    </span>
                    {editable ? (
                      <div className="formInput">
                        <ReInput
                          name="senderAddress"
                          label=""
                          type="simple"
                          textItem="Sender Address"
                          required
                        />
                      </div>
                    ) : form.getFieldValue("senderAddress") ? (
                      <div
                        className="formData"
                        style={{ paddingTop: "4px", paddingBottom: "5px" }}
                      >
                        <styles.ViewOnlyDiv>
                          {form.getFieldValue("senderAddress")}
                        </styles.ViewOnlyDiv>
                      </div>
                    ) : (
                      <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
                    )}
                  </div>

                  <div className="formItem">
                    <span>
                      <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                      Sender City:{" "}
                    </span>
                    {editable ? (
                      <div className="formInput">
                        <ReInput
                          name="senderCity"
                          label=""
                          type="simple"
                          textItem="Sender City"
                          required
                        />
                      </div>
                    ) : form.getFieldValue("senderCity") ? (
                      <div
                        className="formData"
                        style={{ paddingTop: "4px", paddingBottom: "5px" }}
                      >
                        <styles.ViewOnlyDiv>
                          {form.getFieldValue("senderCity")}
                        </styles.ViewOnlyDiv>
                      </div>
                    ) : (
                      <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
                    )}
                  </div>

                  <div className="formItem">
                    <span>
                      <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                      Sender State:{" "}
                    </span>
                    {editable ? (
                      <div className="formInput">
                        <ReInput
                          name="senderState"
                          label=""
                          type="simple"
                          textItem="Sender State"
                          required
                        />
                      </div>
                    ) : form.getFieldValue("senderState") ? (
                      <div
                        className="formData"
                        style={{ paddingTop: "4px", paddingBottom: "5px" }}
                      >
                        <styles.ViewOnlyDiv>
                          {form.getFieldValue("senderState")}
                        </styles.ViewOnlyDiv>
                      </div>
                    ) : (
                      <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
                    )}
                  </div>

                  <div className="formItem">
                    <span>
                      <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                      Sender Postal Code:{" "}
                    </span>
                    {editable ? (
                      <div className="formInput">
                        <ReInput
                          name="senderPostalCode"
                          label=""
                          type="simple"
                          textItem="Sender Postal Code"
                          required
                        />
                      </div>
                    ) : form.getFieldValue("senderPostalCode") ? (
                      <div
                        className="formData"
                        style={{ paddingTop: "4px", paddingBottom: "5px" }}
                      >
                        <styles.ViewOnlyDiv>
                          {form.getFieldValue("senderPostalCode")}
                        </styles.ViewOnlyDiv>
                      </div>
                    ) : (
                      <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
                    )}
                  </div>

                  <div className="formItem">
                    <span>
                      <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                      Sender Country:{" "}
                    </span>
                    {editable ? (
                      <div className="formInput">
                        <ReSelect
                          label=""
                          name="senderCountry"
                          items={country?.map((cou: any) => ({
                            value: cou?.alpha2,
                            title: cou?.name,
                          }))}
                          required
                        />
                      </div>
                    ) : form.getFieldValue("senderCountry") ? (
                      <div
                        className="formData"
                        style={{ paddingTop: "4px", paddingBottom: "5px" }}
                      >
                        <styles.ViewOnlyDiv>
                          {
                            country.find(
                              (co: any) =>
                                co.alpha2 ===
                                form.getFieldValue("senderCountry")
                            )?.name
                          }
                        </styles.ViewOnlyDiv>
                      </div>
                    ) : (
                      <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
                    )}
                  </div>

                  <div className="formItem">
                    <span>
                      <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                      Sender Interchange ID:{" "}
                    </span>
                    {editable ? (
                      <div className="formInput">
                        <ReInput
                          name="senderInterchangeID"
                          label=""
                          type="simple"
                          textItem="Sender Interchange ID"
                          required
                        />
                        <div
                          style={{
                            margin: "0.5rem 0",
                            display: "flex",
                            flexWrap: "wrap",
                            rowGap: "0.5rem",
                          }}
                        ></div>
                      </div>
                    ) : form.getFieldValue("senderInterchangeID") ? (
                      <div
                        className="formData"
                        style={{ paddingTop: "4px", paddingBottom: "5px" }}
                      >
                        <styles.ViewOnlyDiv>
                          {form.getFieldValue("senderInterchangeID")}
                        </styles.ViewOnlyDiv>
                      </div>
                    ) : (
                      <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
                    )}
                  </div>

                  {/* Repeat similar structure for Receiver Information */}
                  <div className="formItem">
                    <span>
                      <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                      Receiver First Name:{" "}
                    </span>
                    {editable ? (
                      <div className="formInput">
                        <ReInput
                          name="receiverFirstName"
                          label=""
                          type="simple"
                          textItem="Receiver First Name"
                          required
                        />
                      </div>
                    ) : form.getFieldValue("receiverFirstName") ? (
                      <div
                        className="formData"
                        style={{ paddingTop: "4px", paddingBottom: "5px" }}
                      >
                        <styles.ViewOnlyDiv>
                          {form.getFieldValue("receiverFirstName")}
                        </styles.ViewOnlyDiv>
                      </div>
                    ) : (
                      <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
                    )}
                  </div>

                  <div className="formItem">
                    <span>
                      <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                      Receiver Last Name:{" "}
                    </span>
                    {editable ? (
                      <div className="formInput">
                        <ReInput
                          name="receiverLastName"
                          label=""
                          type="simple"
                          textItem="Receiver Last Name"
                          required
                        />
                      </div>
                    ) : form.getFieldValue("receiverLastName") ? (
                      <div
                        className="formData"
                        style={{ paddingTop: "4px", paddingBottom: "5px" }}
                      >
                        <styles.ViewOnlyDiv>
                          {form.getFieldValue("receiverLastName")}
                        </styles.ViewOnlyDiv>
                      </div>
                    ) : (
                      <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
                    )}
                  </div>

                  <div className="formItem">
                    <span>
                      <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                      Receiver Address:{" "}
                    </span>
                    {editable ? (
                      <div className="formInput">
                        <ReInput
                          name="receiverAddress"
                          label=""
                          type="simple"
                          textItem="Receiver Address"
                          required
                        />
                      </div>
                    ) : form.getFieldValue("receiverAddress") ? (
                      <div
                        className="formData"
                        style={{ paddingTop: "4px", paddingBottom: "5px" }}
                      >
                        <styles.ViewOnlyDiv>
                          {form.getFieldValue("receiverAddress")}
                        </styles.ViewOnlyDiv>
                      </div>
                    ) : (
                      <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
                    )}
                  </div>

                  <div className="formItem">
                    <span>
                      <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                      Receiver City:{" "}
                    </span>
                    {editable ? (
                      <div className="formInput">
                        <ReInput
                          name="receiverCity"
                          label=""
                          type="simple"
                          textItem="Receiver City"
                          required
                        />
                      </div>
                    ) : form.getFieldValue("receiverCity") ? (
                      <div
                        className="formData"
                        style={{ paddingTop: "4px", paddingBottom: "5px" }}
                      >
                        <styles.ViewOnlyDiv>
                          {form.getFieldValue("receiverCity")}
                        </styles.ViewOnlyDiv>
                      </div>
                    ) : (
                      <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
                    )}
                  </div>

                  <div className="formItem">
                    <span>
                      <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                      Receiver State:{" "}
                    </span>
                    {editable ? (
                      <div className="formInput">
                        <ReInput
                          name="receiverState"
                          label=""
                          type="simple"
                          textItem="Receiver State"
                          required
                        />
                      </div>
                    ) : form.getFieldValue("receiverState") ? (
                      <div
                        className="formData"
                        style={{ paddingTop: "4px", paddingBottom: "5px" }}
                      >
                        <styles.ViewOnlyDiv>
                          {form.getFieldValue("receiverState")}
                        </styles.ViewOnlyDiv>
                      </div>
                    ) : (
                      <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
                    )}
                  </div>

                  <div className="formItem">
                    <span>
                      <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                      Receiver Postal Code:{" "}
                    </span>
                    {editable ? (
                      <div className="formInput">
                        <ReInput
                          name="receiverPostalCode"
                          label=""
                          type="simple"
                          textItem="Receiver Postal Code"
                          required
                        />
                      </div>
                    ) : form.getFieldValue("receiverPostalCode") ? (
                      <div
                        className="formData"
                        style={{ paddingTop: "4px", paddingBottom: "5px" }}
                      >
                        <styles.ViewOnlyDiv>
                          {form.getFieldValue("receiverPostalCode")}
                        </styles.ViewOnlyDiv>
                      </div>
                    ) : (
                      <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
                    )}
                  </div>

                  <div className="formItem">
                    <span>
                      <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                      Receiver Country:{" "}
                    </span>
                    {editable ? (
                      <div className="formInput">
                        <ReSelect
                          label=""
                          name="receiverCountry"
                          items={country?.map((cou: any) => ({
                            value: cou?.alpha2,
                            title: cou?.name,
                          }))}
                          required
                        />
                      </div>
                    ) : form.getFieldValue("receiverCountry") ? (
                      <div
                        className="formData"
                        style={{ paddingTop: "4px", paddingBottom: "5px" }}
                      >
                        <styles.ViewOnlyDiv>
                          {
                            country.find(
                              (co: any) =>
                                co.alpha2 ===
                                form.getFieldValue("receiverCountry")
                            )?.name
                          }
                        </styles.ViewOnlyDiv>
                      </div>
                    ) : (
                      <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
                    )}
                  </div>

                  <div className="formItem">
                    <span>
                      <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                      Receiver Interchange ID:{" "}
                    </span>
                    {editable ? (
                      <div className="formInput">
                        <ReInput
                          name="receiverInterchangeID"
                          label=""
                          type="simple"
                          textItem="Receiver Interchange ID"
                          required
                        />
                      </div>
                    ) : form.getFieldValue("receiverInterchangeID") ? (
                      <div
                        className="formData"
                        style={{ paddingTop: "4px", paddingBottom: "5px" }}
                      >
                        <styles.ViewOnlyDiv>
                          {form.getFieldValue("receiverInterchangeID")}
                        </styles.ViewOnlyDiv>
                      </div>
                    ) : (
                      <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
                    )}
                  </div>

                  <div className="formItem">
                    <span>
                      <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                      XML Doc Type:{" "}
                    </span>

                    {editable ? (
                      <div className="formInput">
                        <ReSelect
                          label=""
                          name="xmlDocType"
                          items={[
                            {
                              name: "E2B R3 Standard",
                              value: "E2B R3 Standard",
                            },
                            {
                              name: "E2B R2 Standard",
                              value: "E2B R2 Standard",
                            },
                            {
                              name: "FDA R3 Standard",
                              value: "FDA R3 Standard",
                            },
                          ]?.map((type: any) => {
                            return {
                              title: type.name,
                              value: type.value,
                            };
                          })}
                          onChange={handleTypeChange}
                          textItem="XML Doc Type"
                          required
                          searchable
                        />
                      </div>
                    ) : form.getFieldValue("xmlDocType") ? (
                      <div
                        className="formData"
                        style={{ paddingTop: "4px", paddingBottom: "5px" }}
                      >
                        <styles.ViewOnlyDiv>
                          {form.getFieldValue("xmlDocType")}
                        </styles.ViewOnlyDiv>
                      </div>
                    ) : (
                      <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
                    )}
                  </div>
                </>
              )}
            </div>


            <div className="formItem">
              <span>
                Site Number:{" "}
              </span>
              {editable ? (
                <div className="formInput">
                  <ReInput
                    name="siteNumber"
                    label=""
                    type="simple"
                    textItem="Site Number"
                  />
                </div>
              ) : form.getFieldValue("siteNumber") ? (
                <div
                  className="formData"
                  style={{ paddingTop: "4px", paddingBottom: "5px" }}
                >
                  <styles.ViewOnlyDiv>
                    {form.getFieldValue("siteNumber")}
                  </styles.ViewOnlyDiv>
                </div>
              ) : (
                <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
              )}
            </div>

            <div className="formItem">
              <span>
                Site Name:{" "}
              </span>
              {editable ? (
                <div className="formInput">
                  <ReInput
                    name="siteName"
                    label=""
                    type="simple"
                    textItem="Site Name"
                  />
                </div>
              ) : form.getFieldValue("siteName") ? (
                <div
                  className="formData"
                  style={{ paddingTop: "4px", paddingBottom: "5px" }}
                >
                  <styles.ViewOnlyDiv>
                    {form.getFieldValue("siteName")}
                  </styles.ViewOnlyDiv>
                </div>
              ) : (
                <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
              )}
            </div>
            <div className="formItem">
              <span>
                Study Number:{" "}
              </span>
              {editable ? (
                <div className="formInput">
                  {isManual ? (
                    <ReInput
                      name="studyNumber"
                      label=""
                      type="simple"
                      textItem="Study Number"
                    />
                  ) : (
                    <ReSelect
                      label=""
                      name="studyNumber"
                      type="multiple"
                      items={allStudies?.map((study: any) => {
                        return {
                          title: study?.studyNumber,
                          value: study?.studyNumber,
                        };
                      })}
                      textItem="studyNumber"
                      searchable
                    />
                  )}
                </div>
              ) : formatStudyNumber(form.getFieldValue("studyNumber")) ? (
                <div
                  className="formData"
                  style={{ paddingTop: "4px", paddingBottom: "5px" }}
                >
                  <styles.ViewOnlyDiv>
                    {formatStudyNumber(form.getFieldValue("studyNumber"))}
                  </styles.ViewOnlyDiv>
                </div>
              ) : (
                <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
              )}
            </div>


            {isSuperAdmin && (
              <div className="formItem" style={{ marginBottom: "1rem" }}>
                <span>
                  <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                  Sponsor/MAH:{" "}
                </span>
                <div className="formInput">
                  {editable ? (
                    <ReSelect
                      label=""
                      name="selectedTargetTenant"
                      type="multiple"
                      items={allSponsors?.map((sp: any) => ({
                        title: sp.name,
                        value: sp.id,
                        disable: sp.status === 0 || false,
                      }))}
                      onChange={(value: any) => {
                        handleSelectChanges("sponsor", value);
                        setSelectedTargetTenant(value);
                      }}
                      textItem="Sponsor/MAH"
                      required
                      searchable
                    />
                  ) : (
                    <styles.ViewOnlyDiv>
                      {selectedTargetTenant
                        ?.map((tId) => {
                          const sponsor = allSponsors.find(
                            (sp: any) => sp.id === tId
                          );
                          return sponsor ? sponsor.name : tId;
                        })
                        .join(", ") || "-"}
                    </styles.ViewOnlyDiv>
                  )}
                </div>
              </div>
            )}

            <div className="formItem">
              <span>
                <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                Role:{" "}
              </span>
              {editable ? (
                <div className="formInput">
                  <ReSelect
                    label=""
                    name="role"
                    type="multiple"
                    items={allRoles?.map((rol: any) => {
                      return {
                        title: rol?.name,
                        value: rol?.id,
                        disable: rol?.status === 0 || false,
                      };
                    })}
                    onChange={(value: any) => {
                      handleSelectChanges("role", value);
                    }}
                    textItem="Role"
                    required
                    searchable
                  />
                </div>
              ) : form.getFieldValue("roleMatrices") ? (
                <div
                  className="formData"
                  style={{ paddingTop: "4px", paddingBottom: "5px" }}
                >
                  {form.getFieldValue("roleMatrices")?.length > 0 ? (
                    form
                      .getFieldValue("roleMatrices")
                      .map((roleMatrix: any) => {
                        const tenantId = JSON.parse(
                          sessionStorage.getItem("selectedTenant") || "{}"
                        )?.id;

                        const userData = loadState()?.session?.userData;
                        const tenantData = userData?.tenants?.find(
                          (t: any) => t.id === tenantId
                        );
                        const tenantName = tenantData
                          ? tenantData.name
                          : "Unknown Tenant";

                        if (roleMatrix.tenant === tenantName) {
                          return (
                            <styles.ViewOnlyDiv>
                              <p key={roleMatrix.tenant} style={{ margin: 0 }}>
                                {`${roleMatrix.roles.join(", ")}`}
                              </p>
                            </styles.ViewOnlyDiv>
                          );
                        }
                        return null;
                      })
                  ) : (
                    <p style={{ margin: 0 }}>No roles available</p> // Fallback message if no roles
                  )}
                </div>
              ) : (
                <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
              )}
            </div>

            {editable && isSuperAdmin && (
              <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
                <span style={{ display: "block", marginBottom: "8px", fontWeight: 500 }}>
                  Role Assignment Matrix:
                </span>
                <UserRoleMatrix
                  key={`matrix-${selectedSponsors.length}-${selectedRoles.length}`}
                  selectedSponsors={selectedSponsors}
                  selectedRoles={selectedRoles}
                  setTempUser={setTempUser}
                  tempUser={tempUser}
                />
              </div>
            )}

            {!editable && (
              <div className="formItem">
                <span>Last Modified Date:</span>
                <div
                  className="formData"
                  style={{ paddingTop: "4px", paddingBottom: "5px" }}
                >
                  <styles.ViewOnlyDiv>
                    {currEditingUser?.updated_at ? (
                      convertDateInReadableFormat(currEditingUser?.updated_at)
                    ) : (
                      <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
                    )}
                  </styles.ViewOnlyDiv>
                </div>
              </div>
            )}
            {/* <ReInput label="Email ID" name="email" type="email" required /> */}
            {/* <ReInput label="Phone Number" name="phoneNumber" type="number" />
          <ReSelect
            label="Sponsor/MAH"
            name="sponsor"
            type="multiple"
            items={allSponsors?.map((sp: any) => {
              return {
                title: sp.name,
                value: sp.id,
                disable: sp.status === 0 || false,
              };
            })}
            onChange={(value: any) => {
              handleSelectChanges("sponsor", value);
            }}
            required
            searchable
          /> */}
            {/* <ReSelect
            label="Role"
            name="role"
            type="multiple"
            items={allRoles?.map((rol: any) => {
              return {
                title: rol.name,
                value: rol.id,
                disable: rol.status === 0 || false,
              };
            })}
            onChange={(value: any) => {
              handleSelectChanges("role", value);
            }}
            required
            searchable
          /> */}

            <Form.Item label="" rules={[{ required: true }]} required>
              <div className="formItem">
                <span>
                  <styles.RequiredAsterisk>*</styles.RequiredAsterisk>
                  Status:{" "}
                </span>
                {editable ? (
                  <div className="formInput">
                    <Switch
                      checked={tempUser?.status === 1}
                      checkedChildren="Active"
                      unCheckedChildren="Inactive"
                      onChange={(checked) => {
                        setTempUser((prevData) => ({
                          ...prevData,
                          status: checked ? 1 : 0,
                        }));
                      }}
                    />
                  </div>
                ) : (form.getFieldValue("status") !== undefined && form.getFieldValue("status") !== null) ? (
                  <div
                    className="formData"
                    style={{ paddingTop: "4px", paddingBottom: "5px" }}
                  >
                    {" "}
                    <styles.ViewOnlyDiv>
                      {form.getFieldValue("status") === 1
                        ? "Active"
                        : "Inactive"}
                    </styles.ViewOnlyDiv>
                  </div>
                ) : (
                  <styles.ViewOnlyDiv>{"-"}</styles.ViewOnlyDiv>
                )}
              </div>
              {drawerType !== "create" &&
                (!editable ? (
                  <Button
                    style={{ marginTop: "1rem" }}
                    type="primary"
                    size="middle"
                    onClick={() => {
                      setEditable(true);
                      // Manual mode: swap the merged view for the edited record's own values.
                      form.setFieldsValue(siteFieldValues(true));
                    }}
                  >
                    Edit
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      setEditable(false);
                      handleRefresh(false);
                    }}
                  >
                    Back
                  </Button>
                ))}
              {editable && (
                <Button
                  style={{
                    marginTop: "1rem",
                    marginLeft: `${drawerType !== "create" && "0.5rem"}`,
                  }}
                  size="middle"
                  loading={submitLoading}
                  type="primary"
                  onClick={() => {
                    form.submit();
                  }}
                >
                  {drawerType !== "create" ? "Update" : "Create"}
                </Button>
              )}
            </Form.Item>
          </ReusableForm>
        </Drawer>
      </div>
    </styles.Container>
  );
};
